import React, { useEffect, useMemo, useState } from "react";
import "./UserMessages.css";
import Table from "../../../../components/reusable-table/Table";
import Input from "../../../../components/reusable-input/Input";
import UserModal from "../user-modal/UserModal";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumb from "../../../../components/custom-breadcrumb/Breadcrumb";
import { SearchIcon } from "../../../../icons/reactIcons";
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks";
import {
  getCmsMessages,
  sendCmsMessage,
} from "../../../../redux/slices/cmsMessageSlice";
import { formatDateTime } from "../../../../utils/formatDateTime";

const columns = [
  { header: "Message", accessor: "message" },
  { header: "Created By", accessor: "createdBy" },
];

const UserMessages: React.FC = () => {
  const { userId, name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // ✅ Get studyId from router state
  const studyId = location.state?.studyId || "";

  const { list, studyTeam, loading, createLoading } = useAppSelector(
    (state) => state.cmsMessages,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const adminDetail = JSON.parse(localStorage.getItem("adminDetail") || "{}");
  const userRole = adminDetail.role || "";
  const isStudyTeam = userRole === "study team";

  useEffect(() => {
    if (isStudyTeam) {
      dispatch(getCmsMessages({ type: "study-team", pageId: 1, limit: 100 }));
    } else {
      dispatch(getCmsMessages({ type: "list", pageId: 1, limit: 100 }));
    }
  }, [dispatch, isStudyTeam]);

  const filteredMessages = useMemo(() => {
    if (!userId) return [];

    const source = isStudyTeam ? studyTeam : list;

    const result: any[] = [];

    source?.forEach((group: any) => {
      group?.messages?.forEach((msg: any) => {
        const isUserMatched = msg.users?.some(
          (u: any) => String(u.userId) === String(userId),
        );

        if (isUserMatched) {
          result.push({
            message: msg.message,
            createdBy: (
              <div className="merged-cell">
                <div className="merged-label">{msg.createdBy}</div>
                <div className="merged-date">
                  {formatDateTime(msg.createdDate)}
                </div>
              </div>
            ),
          });
        }
      });
    });

    return result;
  }, [list, studyTeam, userId, isStudyTeam]);

  // --------------------------------
  // SEARCH FILTER
  // --------------------------------
  const searchedData = useMemo(() => {
    if (!searchTerm.trim()) return filteredMessages;

    return filteredMessages.filter((item) =>
      item.message.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [filteredMessages, searchTerm]);

  const breadcrumbItems = [
    { label: "Users", onClick: () => navigate("/users") },
    {
      label: name ?? "",
      onClick: () => navigate(`/users/${name}/${userId}`),
    },
    { label: "Messages", disabled: true },
  ];

  // ✅ SEND MESSAGE INTEGRATION
  const handleSendMessage = async (message: string) => {
    if (!userId) return;

    try {
      await dispatch(
        sendCmsMessage({
          message,
          users: [
            {
              id: String(userId),
              isActive: "true",
            },
          ],
        }),
      ).unwrap();

      // ✅ Refresh messages after sending
      if (isStudyTeam) {
        dispatch(getCmsMessages({ type: "study-team", pageId: 1, limit: 100 }));
      } else {
        dispatch(getCmsMessages({ type: "list", pageId: 1, limit: 100 }));
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error("Send message failed:", error);
      // Modal stays open for retry
    }
  };

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <div className="user-messages-container">
        <div className="user-messages-header">
          <h2>Messages</h2>

          <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
            <Input
              type="text"
              placeholder="Search"
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
              search
              startIcon={<SearchIcon />}
              removeBottomMargin
            />

            <button
              className="user-messages-btn"
              onClick={() => setIsModalOpen(true)}
            >
              Send Message
            </button>
          </div>
        </div>

        <Table
          columns={columns}
          data={searchedData}
          rowsPerPage={20}
          rounded
          loading={loading}
        />

        {isModalOpen && (
          <UserModal
            type="message"
            name={name ?? ""}
            studyId={studyId} // ✅ now coming from router state
            loading={createLoading}
            onClose={() => setIsModalOpen(false)}
            onSend={handleSendMessage}
          />
        )}
      </div>
    </>
  );
};

export default UserMessages;
